import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useMiniAppApi } from '../lib/context';
import { useAuthState } from '../lib/auth-context';

const FALLBACK_TIMEZONES = [
  'UTC',
  'Europe/Moscow',
  'Europe/Berlin',
  'Asia/Tokyo',
  'America/New_York',
];

export default function SettingsView() {
  const api = useMiniAppApi();
  const auth = useAuthState();
  const userIdentifier = auth.session?.userId ?? auth.session?.telegramUserId;
  const canQuery =
    !!userIdentifier &&
    (auth.status === 'authenticated' ||
      auth.status === 'cached-session' ||
      auth.status === 'read-only');

  const timezones = useMemo(() => {
    const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    if (typeof intl.supportedValuesOf === 'function') {
      return intl.supportedValuesOf('timeZone');
    }
    console.warn('Intl.supportedValuesOf not available in this environment');
    return FALLBACK_TIMEZONES;
  }, []);

  const settingsQuery = useQuery({
    queryKey: ['settings', userIdentifier],
    queryFn: () => api.getSettings(userIdentifier || undefined),
    enabled: canQuery,
  });

  // Write operations disabled - read-only mode

  if (!userIdentifier) {
    return (
      <div className="card card--info">
        <h2>Настройки</h2>
        {auth.status === 'needs-initdata' && (
          <>
            <p>Чтобы получить настройки, откройте мини-приложение через кнопку в боте.</p>
            <button onClick={auth.retry} style={{ marginTop: '1rem' }}>
              Проверить снова
            </button>
          </>
        )}
        {auth.status === 'read-only' && (
          <p>Настройки доступны только внутри Telegram. Запустите мини-приложение в чате с ботом.</p>
        )}
        {auth.status === 'error' && (
          <>
            <p>Не удалось авторизоваться. {auth.message ?? 'Попробуйте снова позже.'}</p>
            <button onClick={auth.retry} style={{ marginTop: '1rem' }}>
              Повторить
            </button>
          </>
        )}
        {auth.status !== 'needs-initdata' &&
          auth.status !== 'read-only' &&
          auth.status !== 'error' && (
            <p>Для отображения настроек требуется авторизация через Telegram.</p>
          )}
      </div>
    );
  }

  if (settingsQuery.isLoading) {
    return <div className="card">Загружаем настройки…</div>;
  }

  if (settingsQuery.isError) {
    const error = settingsQuery.error as AxiosError | Error;
    const isAxios = 'isAxiosError' in (error as AxiosError) && (error as AxiosError).isAxiosError;
    const axiosError = isAxios ? (error as AxiosError) : null;

    const title = axiosError?.response ? 'Ошибка API' : 'Ошибка сети';
    const message = axiosError?.response
      ? `Сервер ответил статусом ${axiosError.response.status}. Попробуйте позже.`
      : 'Не удалось связаться с сервером. Проверьте подключение и попробуйте снова.';

    return (
      <div className="card card--error">
        <h3>{title}</h3>
        <p>{message}</p>
        <button onClick={() => settingsQuery.refetch()}>Повторить</button>
      </div>
    );
  }

  const isReadOnly = auth.status === 'read-only' || auth.status === 'needs-initdata';

  return (
    <div className="card">
      <h2>Настройки</h2>
      {isReadOnly && (
        <div className="card--info" style={{ marginBottom: '1rem', padding: '1rem', background: '#f0f0f0', borderRadius: '8px' }}>
          <p>Этот интерфейс работает только в режиме чтения. Для изменения настроек используйте Telegram бота (команда /tz).</p>
        </div>
      )}
      <label className="field">
        <span>Часовой пояс</span>
        <select
          value={settingsQuery.data?.timezone ?? 'UTC'}
          disabled={isReadOnly}
          title={isReadOnly ? "Используйте Telegram бота для изменения часового пояса" : undefined}
        >
          {timezones.map(tz => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </label>
      <div className="hint" style={{ marginTop: '0.5rem', color: '#666' }}>
        Текущий часовой пояс: {settingsQuery.data?.timezone ?? 'UTC'}
      </div>
    </div>
  );
}
